import { useCallback, useSyncExternalStore } from "react";
import { useIndexedDB } from "react-indexed-db-hook";
import { OfflineData, BatchSyncFlagBagRequest } from "services/api/flagbags/types";
import { FlagBag } from "services/api/flagbags/types";
import { flagBagApi } from "services/api/flagbags";
import useFlagBagLogController, { FlagbagLogEntry } from "./useFlagBagLogController";
import { loadConflictsFromStorage, saveConflictsToStorage, ConflictItem } from "utils/dataConflictDetector";
import { initIndexedDbIfMissingStore } from "utils/indexedDbRecovery";

export enum SyncStatus {
    SYNCING = "syncing",
    PENDING = "pending",
    SUCCESS = "success",
    FAILED = "failed",
    PARTIAL_SUCCESS = "partial_success",
}

export interface SyncError {
    flagbagId: string;
    flagbagNum: string;
    reason: string;
}

export interface PendingDataInfo {
    syncStatus: SyncStatus;
    syncedCount: number;
    totalCount: number;
    pendingCount: number;
    failedCount: number;  // 新增：失败的数据数量
    userId: string | null;
    userName: string | null;
    flagbagList: FlagBag[];
    errors?: SyncError[];
}

let globalSyncStatus: SyncStatus = SyncStatus.SUCCESS;
let globalSyncProgress = { synced: 0, total: 0 };
let globalSyncFlagbagList: FlagBag[] = [];
let globalSyncErrors: SyncError[] = [];
let lastSyncCompletionTime: number = 0; // Track when sync last completed

const listeners = new Set<() => void>();

let cachedSnapshot: {
    status: SyncStatus;
    progress: { synced: number; total: number };
    flagbagList: FlagBag[];
    errors: SyncError[];
} = {
    status: globalSyncStatus,
    progress: globalSyncProgress,
    flagbagList: globalSyncFlagbagList,
    errors: globalSyncErrors,
};

function emitChange() {
    cachedSnapshot = {
        status: globalSyncStatus,
        progress: globalSyncProgress,
        flagbagList: globalSyncFlagbagList,
        errors: globalSyncErrors,
    };
    listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

function getSnapshot() {
    return cachedSnapshot;
}

function toSyncIsoTimestamp(value?: string): string | undefined {
    if (value == null || value === "") {
        return undefined;
    }
    const trimmed = String(value).trim();
    const numeric = parseFloat(trimmed);
    if (!Number.isNaN(numeric) && /^-?\d+(\.\d+)?$/.test(trimmed)) {
        const ms = numeric < 1e11 ? numeric * 1000 : numeric;
        return new Date(ms).toISOString();
    }
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

function mapLogEntryToSync(log: FlagbagLogEntry) {
    return {
        id: log.id,
        flagbagId: log.flagbagId,
        status: log.status,
        volunteerId: log.volunteerId,
        stationId: log.stationId,
        createdAt: log.createdAt?.toISOString() || new Date(log.timestamp).toISOString(),
    };
}

function mapLogEntryToSyncUpdate(
    log: FlagbagLogEntry,
    offlineData?: OfflineData
) {
    const timestamp = log.createdAt?.toISOString() || new Date(log.timestamp).toISOString();

    const statusToTimestampField: Record<string, string> = {
        assigned: "assignedAt",
        distributed: "distributedAt",
        distributed_other: "distributedOtherAt",
        collected: "collectedAt",
        collected_sub_to_bank: "collectedSubToBankAt",
        collected_bank: "collectedSubToBankAt",
        collected_empty: "collectedEmptyAt",
        collectedEmpty: "collectedEmptyAt",
        collected_other: "collectedOtherAt",
        collectedOther: "collectedOtherAt",
        lost: "lostAt",
        late: "latedAt",
        lated: "latedAt",
    };

    const timestampField = statusToTimestampField[log.status];

    const update: any = {
        flagbagId: log.flagbagId,
        status: log.status,
        volunteerId: log.volunteerId || offlineData?.volunteerId || "",
        stationId: log.stationId,
        finalStationId: offlineData?.finalStationId,
        lastSyncedAt: toSyncIsoTimestamp(offlineData?.lastSyncedAt),
    };

    if (timestampField) {
        update[timestampField] = timestamp;
    }

    if ((offlineData as { remarks?: string })?.remarks) {
        update.remarks = (offlineData as { remarks?: string }).remarks;
    }

    return update;
}

function mapOfflineActionToSyncUpdate(action: OfflineData) {
    return {
        flagbagId: action.flagBagID,
        status: action.status,
        volunteerId: action.volunteerId || "",
        stationId: action.stationID,
        finalStationId: action.finalStationId,
        assignedAt: action.assignedAt,
        sparedAt: action.sparedAt,
        distributedAt: action.distributedAt,
        distributedOtherAt: action.distributedOtherAt,
        collectedAt: action.collectedAt,
        collectedSubToBankAt: action.collectedSubToBankAt,
        collectedEmptyAt: action.collectedEmptyAt,
        collectedOtherAt: action.collectedOtherAt,
        lostAt: action.lostAt,
        latedAt: action.latedAt,
        remarks: (action as { remarks?: string }).remarks,
        lastSyncedAt: toSyncIsoTimestamp(action.lastSyncedAt),
    };
}

export const usePendingQueue = () => {
    const offlineDB = initIndexedDbIfMissingStore("offline");
    const flagBagDB = initIndexedDbIfMissingStore("flagbag");
    const { getAllLogs, clearAllLogs, deleteLogsByFlagbagId } = useFlagBagLogController();

    const snapshot = useSyncExternalStore(subscribe, getSnapshot);
    const syncStatus = snapshot.status;
    const syncProgress = snapshot.progress;
    const syncErrors = snapshot.errors;

    const getPendingDataInfo = useCallback(async (): Promise<PendingDataInfo> => {
        try {
            // Add a delay if we just finished syncing (within last 1 second) to ensure IndexedDB operations are complete
            // This prevents reading stale data immediately after a sync operation
            const timeSinceLastSync = Date.now() - lastSyncCompletionTime;
            
            if (timeSinceLastSync < 1000 && timeSinceLastSync > 0) {
                // Sync completed recently, wait for IndexedDB to fully commit changes
                await new Promise(resolve => setTimeout(resolve, 250));
            }
            
            const pendingActions: OfflineData[] = await offlineDB.getAll();

            const totalCount = pendingActions?.length || 0;

            const failedActions = pendingActions.filter(action => action.syncError);
            const persistedFailedCount = failedActions.length;

            const currentSessionErrors = globalSyncErrors?.length || 0;

            const failedCount = Math.max(persistedFailedCount, currentSessionErrors);

            const pendingCount = Math.max(0, totalCount - failedCount);

            const errors: SyncError[] = failedActions.map(action => ({
                flagbagId: action.flagBagID,
                flagbagNum: action.flagbagNum || action.flagBagID,
                reason: action.syncError || 'Unknown error',
            }));

            if (totalCount === 0) {
                return {
                    syncStatus: SyncStatus.SUCCESS,
                    syncedCount: 0,
                    totalCount: 0,
                    pendingCount: 0,
                    failedCount: 0,
                    userId: null,
                    userName: null,
                    flagbagList: [],
                    errors: [],
                };
            }

            const firstAction = pendingActions[0];

            // 确定同步状态
            let syncStatus = globalSyncStatus;
            if (failedCount > 0 && pendingCount === 0) {
                syncStatus = SyncStatus.FAILED;
            } else if (failedCount > 0 && pendingCount > 0) {
                syncStatus = SyncStatus.PARTIAL_SUCCESS;
            } else if (pendingCount > 0) {
                syncStatus = SyncStatus.PENDING;
            }

            return {
                syncStatus: syncStatus,
                syncedCount: globalSyncProgress.synced,
                totalCount: globalSyncProgress.total || totalCount,
                pendingCount: pendingCount,
                failedCount: failedCount,
                userId: firstAction.volunteerId || null,
                userName: firstAction.userName || null,
                flagbagList: globalSyncFlagbagList,
                errors: errors.length > 0 ? errors : globalSyncErrors,
            };
        } catch (error) {
            return {
                syncStatus: SyncStatus.FAILED,
                syncedCount: 0,
                totalCount: 0,
                pendingCount: 0,
                failedCount: 0,
                userId: null,
                userName: null,
                flagbagList: [],
                errors: [],
            };
        }
    }, [offlineDB]);

    const setSyncStatus = useCallback((status: SyncStatus) => {
        globalSyncStatus = status;
        emitChange();
    }, []);

    const startSync = useCallback(async () => {
        const pendingActions: OfflineData[] = await offlineDB.getAll();
        const total = pendingActions?.length || 0;
        try {
            globalSyncProgress = { synced: 0, total };
            globalSyncStatus = SyncStatus.SYNCING;
            emitChange();
            return total;
        } catch (error) {
            globalSyncStatus = SyncStatus.FAILED;
            emitChange();
            throw error;
        }
    }, [offlineDB]);

    const updateSyncProgress = useCallback((synced: number) => {
        globalSyncProgress = { ...globalSyncProgress, synced };
        emitChange();
    }, []);

    const resetSyncProgress = useCallback(() => {
        globalSyncProgress = { synced: 0, total: 0 };
        globalSyncErrors = [];
        emitChange();
    }, []);

    const batchSync = useCallback(async (
        eventId: string,
        batchSize: number = 50,
        flagBagDB?: any
    ): Promise<{
        totalSuccess: number;
        totalFailed: number;
        errors: SyncError[];
    }> => {
        try {
            const pendingActions: OfflineData[] = await offlineDB.getAll();

            if (flagBagDB) {
                for (const action of pendingActions) {
                    if (!action.flagbagNum) {
                        try {
                            const flagbag = await flagBagDB.getByID(action.flagBagID);
                            if (flagbag && (flagbag.flagbagNum || flagbag.flagBagNum)) {
                                action.flagbagNum = flagbag.flagbagNum || flagbag.flagBagNum;
                            }
                        } catch (err) {
                            console.warn(`Failed to get flagbagNum for ${action.flagBagID}`);
                        }
                    }
                }
            }

            if (pendingActions.length === 0) {
                globalSyncStatus = SyncStatus.SUCCESS;
                globalSyncProgress = { synced: 0, total: 0 };
                globalSyncErrors = [];
                emitChange();
                return { totalSuccess: 0, totalFailed: 0, errors: [] };
            }

            const totalCount = pendingActions.length;
            globalSyncProgress = { synced: 0, total: totalCount };
            globalSyncStatus = SyncStatus.SYNCING;
            globalSyncErrors = [];
            emitChange();

            let totalSuccess = 0;
            let totalFailed = 0;
            const allErrors: SyncError[] = [];

            // Get all logs from flagbaglog
            const allLogs = await getAllLogs();
            const logsForSync = allLogs.map(mapLogEntryToSync);

            // Group logs by flagbagId and sort by timestamp for ordered updates
            const logsByFlagbagId = new Map<string, FlagbagLogEntry[]>();
            for (const log of allLogs) {
                const existing = logsByFlagbagId.get(log.flagbagId) || [];
                existing.push(log);
                logsByFlagbagId.set(log.flagbagId, existing);
            }
            Array.from(logsByFlagbagId.values()).forEach((logs) => {
                logs.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
            });

            // Build a lookup map from flagbagId to its offline data (for enriching updates)
            const offlineDataMap = new Map<string, OfflineData>();
            for (const action of pendingActions) {
                offlineDataMap.set(action.flagBagID, action);
            }

            for (let i = 0; i < pendingActions.length; i += batchSize) {
                const batch = pendingActions.slice(i, i + batchSize);
                const batchFlagbagIds = new Set(batch.map((a) => a.flagBagID));

                // Build updates from logs so every intermediate status is included.
                // If a flagbag has no logs, fall back to its offline data.
                const updates: BatchSyncFlagBagRequest["updates"] = [];
                for (const flagbagId of Array.from(batchFlagbagIds)) {
                    const logs = logsByFlagbagId.get(flagbagId) || [];
                    const offlineData = offlineDataMap.get(flagbagId);
                    if (logs.length > 0) {
                        for (const log of logs) {
                            updates.push(mapLogEntryToSyncUpdate(log, offlineData));
                        }
                    } else if (offlineData) {
                        updates.push(mapOfflineActionToSyncUpdate(offlineData));
                    }
                }

                const payload: BatchSyncFlagBagRequest = {
                    logs: logsForSync,
                    updates,
                };

                try {
                    const response = await flagBagApi.batchSyncFlagBag(eventId, payload);

                    const responseData: any = response.data || response;

                    if (responseData && (response.code === '0' || responseData.successCount !== undefined)) {
                        totalSuccess += responseData.successCount || 0;
                        totalFailed += responseData.errorCount || 0;

                        const failedIds = new Set<string>();
                        const errorMap = new Map<string, { reason: string; flagbagNum: string }>();

                        if (responseData.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
                            for (const error of responseData.errors) {
                                if (error && error.flagbagId) {
                                    failedIds.add(error.flagbagId);
                                    const reason = error.reason || 'Unknown error';
                                    const flagbagNum = error.flagbagNum || error.flagbagId;

                                    errorMap.set(error.flagbagId, { reason, flagbagNum });
                                    allErrors.push({
                                        flagbagId: error.flagbagId,
                                        flagbagNum: flagbagNum,
                                        reason: reason,
                                    });

                                    // Update conflict with server status from error response
                                    if (error.currentStatus) {
                                        try {
                                            const conflicts = loadConflictsFromStorage() || [];
                                            const conflictIndex = conflicts.findIndex((c: ConflictItem) => c.flagBagId === error.flagbagId);
                                            
                                            // Get local status from flagbag
                                            const flagbag = await flagBagDB.getByID(error.flagbagId);
                                            const localStatus = flagbag?.status?.toString() || flagbag?.currentStatus?.toString() || '';

                                            if (conflictIndex !== -1) {
                                                // Update existing conflict
                                                conflicts[conflictIndex].serverStatus = error.currentStatus;
                                            } else {
                                                // Create new conflict
                                                conflicts.push({
                                                    flagBagId: error.flagbagId,
                                                    localStatus: localStatus,
                                                    serverStatus: error.currentStatus,
                                                    reason: error.reason,
                                                    serverTimestamp: error.updatedAt,
                                                    serverUpdatedBy: error.updatedBy,
                                                });
                                            }
                                            
                                            saveConflictsToStorage(conflicts);
                                        } catch (conflictError) {
                                            console.warn(`Failed to update conflict for flagbag ${error.flagbagId}:`, conflictError);
                                        }
                                    }
                                }
                            }
                        }

                        for (const action of batch) {
                            if (failedIds.has(action.flagBagID)) {
                                try {
                                    const errorInfo = errorMap.get(action.flagBagID);
                                    await offlineDB.update({
                                        ...action,
                                        flagbagNum: errorInfo?.flagbagNum || action.flagbagNum || action.flagBagID,
                                        syncError: errorInfo?.reason || 'Unknown error',
                                        syncFailedAt: Date.now(),
                                    });
                                } catch (updateError) {
                                    console.error(`Failed to update error info for ${action.flagBagID}:`, updateError);
                                }
                            }
                        }

                        // Delete successful records using their composite ID
                        const successActions = batch.filter(action => !failedIds.has(action.flagBagID));

                        // Delete records and wait for all deletions to complete
                        const deletePromises = successActions.map(action => 
                            offlineDB.deleteRecord(action.id).catch(deleteError => {
                                console.error(`Failed to delete record ${action.flagBagID} (${action.id}):`, deleteError);
                            })
                        );
                        await Promise.all(deletePromises);

                        for (const action of successActions) {
                            await deleteLogsByFlagbagId(action.flagBagID);
                        }
                        
                        // Give IndexedDB time to commit the transaction
                        await new Promise(resolve => setTimeout(resolve, 50));
                    } else {
                        totalFailed += batch.length;
                        const errorReason = response.message || 'Unknown error';

                        for (const action of batch) {
                            allErrors.push({
                                flagbagId: action.flagBagID,
                                flagbagNum: action.flagbagNum || action.flagBagID,
                                reason: errorReason,
                            });

                            try {
                                await offlineDB.update({
                                    ...action,
                                    syncError: errorReason,
                                    syncFailedAt: Date.now(),
                                });
                            } catch (updateError) {
                                console.error(`Failed to update error info for ${action.flagBagID}:`, updateError);
                            }
                        }
                    }

                    const currentSynced = Math.min(i + batchSize, totalCount);
                    globalSyncProgress = { synced: currentSynced, total: totalCount };
                    emitChange();

                } catch (error) {
                    console.error('Batch sync error:', error);
                    totalFailed += batch.length;
                    const errorReason = error instanceof Error ? error.message : 'Network error';

                    for (const action of batch) {
                        allErrors.push({
                            flagbagId: action.flagBagID,
                            flagbagNum: action.flagbagNum || action.flagBagID,
                            reason: errorReason,
                        });

                        try {
                            await offlineDB.update({
                                ...action,
                                syncError: errorReason,
                                syncFailedAt: Date.now(),
                            });
                        } catch (updateError) {
                            console.error(`Failed to update error info for ${action.flagBagID}:`, updateError);
                        }
                    }
                }
            }

            globalSyncErrors = allErrors;

            if (totalFailed === 0) {
                globalSyncStatus = SyncStatus.SUCCESS;
                await clearAllLogs();
            } else if (totalSuccess > 0) {
                globalSyncStatus = SyncStatus.PARTIAL_SUCCESS;
            } else {
                globalSyncStatus = SyncStatus.FAILED;
            }

            // Ensure all IndexedDB operations are complete before emitting changes
            // Wait a bit more to ensure database transactions are committed
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Mark sync completion time for getPendingDataInfo to know when to add delay
            lastSyncCompletionTime = Date.now();
            
            emitChange();

            return {
                totalSuccess,
                totalFailed,
                errors: allErrors,
            };

        } catch (error) {
            globalSyncStatus = SyncStatus.FAILED;
            
            // Mark sync completion time even for failures
            lastSyncCompletionTime = Date.now();
            
            emitChange();
            throw error;
        }
    }, [offlineDB, getAllLogs, clearAllLogs, deleteLogsByFlagbagId]);

    const retryFailedSync = useCallback(async (
        eventId: string,
        failedIds: string[]
    ): Promise<{
        success: number;
        failed: number;
        errors: SyncError[];
    }> => {
        try {
            const pendingActions: OfflineData[] = await offlineDB.getAll();
            const retryActions = pendingActions.filter(action =>
                failedIds.includes(action.flagBagID)
            );

            if (retryActions.length === 0) {
                return { success: 0, failed: 0, errors: [] };
            }

            globalSyncStatus = SyncStatus.SYNCING;
            emitChange();

            // Get all logs from flagbaglog
            const allLogs = await getAllLogs();
            const logsForSync = allLogs.map(mapLogEntryToSync);

            // Group logs by flagbagId and sort by timestamp for ordered updates
            const logsByFlagbagId = new Map<string, FlagbagLogEntry[]>();
            for (const log of allLogs) {
                const existing = logsByFlagbagId.get(log.flagbagId) || [];
                existing.push(log);
                logsByFlagbagId.set(log.flagbagId, existing);
            }
            Array.from(logsByFlagbagId.values()).forEach((logs) => {
                logs.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
            });

            const updates: BatchSyncFlagBagRequest["updates"] = [];
            for (const action of retryActions) {
                const logs = logsByFlagbagId.get(action.flagBagID) || [];
                if (logs.length > 0) {
                    for (const log of logs) {
                        updates.push(mapLogEntryToSyncUpdate(log, action));
                    }
                } else {
                    updates.push({
                        ...mapOfflineActionToSyncUpdate(action),
                        status: action.status,
                    });
                }
            }

            const payload: BatchSyncFlagBagRequest = {
                logs: logsForSync,
                updates,
            };

            const response = await flagBagApi.batchSyncFlagBag(eventId, payload);

            const responseData: any = response.data || response;

            if (responseData && (response.code === '0' || responseData.successCount !== undefined)) {
                const errors: SyncError[] = [];
                const failedIds = new Set<string>();

                if (responseData.errors) {
                    const errorArray = Array.isArray(responseData.errors)
                        ? responseData.errors
                        : [responseData.errors];

                    errorArray.forEach((error: any) => {
                        if (error && error.flagbagId) {
                            failedIds.add(error.flagbagId);
                            errors.push({
                                flagbagId: error.flagbagId,
                                flagbagNum: error.flagbagNum || error.flagbagId,
                                reason: error.reason || 'Unknown error',
                            });
                        }
                    });
                }

                // Delete successful records using their composite ID
                const successActions = retryActions.filter(action => !failedIds.has(action.flagBagID));

                for (const action of successActions) {
                    try {
                        await offlineDB.deleteRecord(action.id);
                    } catch (deleteError) {
                        console.error(`Failed to delete record ${action.flagBagID} (${action.id}):`, deleteError);
                    }
                }

                for (const action of successActions) {
                    await deleteLogsByFlagbagId(action.flagBagID);
                }

                if (responseData.errorCount === 0) {
                    await clearAllLogs();
                }

                globalSyncStatus = responseData.errorCount > 0
                    ? SyncStatus.PARTIAL_SUCCESS
                    : SyncStatus.SUCCESS;
                globalSyncErrors = errors;
                
                // Mark sync completion time for getPendingDataInfo to know when to add delay
                lastSyncCompletionTime = Date.now();
                
                emitChange();

                return {
                    success: response.data.successCount,
                    failed: response.data.errorCount,
                    errors,
                };
            }

            throw new Error(response.message || 'Retry failed');

        } catch (error) {
            globalSyncStatus = SyncStatus.FAILED;
            
            // Mark sync completion time even for failures
            lastSyncCompletionTime = Date.now();
            
            emitChange();
            throw error;
        }
    }, [offlineDB, getAllLogs, clearAllLogs, deleteLogsByFlagbagId]);

    const loadSyncListFromDB = useCallback(async () => {
        try {
            const pendingActions: OfflineData[] = await offlineDB.getAll();
            globalSyncFlagbagList = pendingActions.map(action => ({
                id: action.flagBagID,
            } as FlagBag));
            emitChange();
        } catch (error) {
        }
    }, [offlineDB]);

    const addFlagbagToSyncList = useCallback(async (flagbag: FlagBag) => {
        try {
            globalSyncFlagbagList = [...globalSyncFlagbagList, flagbag];
            emitChange();

        } catch (error) {
            globalSyncFlagbagList = globalSyncFlagbagList.filter(item => item.id !== flagbag.id);
            emitChange();
            throw error;
        }
    }, [offlineDB]);

    const removeFlagbagFromSyncList = useCallback(async (flagbagId: string) => {
        const oldList = [...globalSyncFlagbagList];

        try {
            globalSyncFlagbagList = globalSyncFlagbagList.filter((item) => item.id !== flagbagId);
            emitChange();

            // Delete all offline records for this flagbagId (there may be multiple status changes)
            const allOfflineRecords: OfflineData[] = await offlineDB.getAll();
            const recordsToDelete = allOfflineRecords.filter(record => record.flagBagID === flagbagId);
            
            for (const record of recordsToDelete) {
                await offlineDB.deleteRecord(record.id);
            }
        } catch (error) {
            globalSyncFlagbagList = oldList;
            emitChange();
            throw error;
        }
    }, [offlineDB]);

    const clearSyncList = useCallback(async () => {
        try {
            globalSyncFlagbagList = [];
            emitChange();

            await offlineDB.clear();
        } catch (error) {
            throw error;
        }
    }, [offlineDB]);

    return {
        getPendingDataInfo,
        syncProgress,
        syncStatus,
        syncErrors,
        setSyncStatus,

        startSync,
        batchSync,
        retryFailedSync,
        updateSyncProgress,
        resetSyncProgress,

        loadSyncListFromDB,
        addFlagbagToSyncList,
        removeFlagbagFromSyncList,
        clearSyncList,
    };
};
